import { Inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Dictionary } from '@ngrx/entity';
import { DragModifiers, Filter, IndexPath, ItemSelectionStore, UniversalFilterStore } from '@shared/ui';
import {
  CropOverlayService,
  type IAssetItem,
  LabelingService,
  LabelOptions,
  Roi,
  RoiChange,
  type RoiCropBox,
  RoiFilteringService,
  unlabeled,
} from '@analysis/domain';
import { ListChangedRoisUseCase } from '@analysis/use-cases';
import { ProtocolStore } from '@telespot/protocols/ui';
import { AssetStore, RoiStatsStore, MosaicPaginationStore, RoiChangeStore, RoiStore } from '../../stores';
import { FilterAutocompleteEffects } from '../filter-autocomplete';
import { LIST_CHANGED_ROIS_USE_CASE } from '../roi-revert/roi-revert.effects';
import { MosaicGalleryHeaderData } from '../../components/mosaic-section-header';
import { MosaicGalleryRowNumberData } from '../../components/mosaic-row-number';
import { GridIndexPath } from '@shared/ui';
import { MosaicCellData } from '../../components/mosaic-cell';
import { BehaviorSubject, EMPTY, from, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, first, map, switchMap, tap } from 'rxjs/operators';
import { Option } from '@telespot/protocols/data-access';

export interface DragSelectionInput {
  indexPaths: GridIndexPath[];
  cellsPerRow: number;
  snapshot: ReadonlySet<string>;
  modifiers: DragModifiers;
}

@Injectable()
export class RoiFilteringEffects {
  /**
   * Whether cells draw the roi's box over the crop. A cell view model source
   * rather than an input on the cell: cells render inside the grid's embedded
   * views, so a re-emission is what repaints them.
   */
  // Off until the tile settings say otherwise, matching the dashboard's own default: the
  // gallery's `roiBoxVisible` input only lands on the first change detection, and cells can
  // be built before it does.
  private readonly _roiBoxVisible$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly filterAutocompleteEffects: FilterAutocompleteEffects,
    private readonly universalFilterStore: UniversalFilterStore,
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly paginationStore: MosaicPaginationStore,
    private readonly assetStore: AssetStore,
    private readonly protocolStore: ProtocolStore,
    private readonly itemSelectionStore: ItemSelectionStore,
    @Inject(LIST_CHANGED_ROIS_USE_CASE)
    private readonly listChangedRoisUseCase: ListChangedRoisUseCase
  ) {
    this.wireHydrateChangedRois();
  }

  private readonly galleryLabelIndex$ = this.roiStore.select(
    this.roiStore.labelIndex$,
    this.paginationStore.labelProgress$,
    this.paginationStore.nextLabel$,
    (labelIndex, labelProgress, nextLabel) => {
      const result = new Map<string, Set<string>>();

      for (const [id, rois] of labelIndex.entries()) {
        const progress = labelProgress[id];

        if (rois.size === 0 || id === unlabeled) continue;
        if (!progress?.complete && id !== nextLabel) continue;

        result.set(id, rois);
      }

      return result;
    }
  );

  public readonly filteredLabelIndex$ = this.roiStore.select(
    this.universalFilterStore.filters$,
    this.roiStore.rois$,
    this.roiStore.labelIndex$,
    this.galleryLabelIndex$,
    this.roiStore.assetIndex$,
    this.roiChangeStore.changedRois$,
    this.protocolStore.options$,
    this.itemSelectionStore.selectedItems$,
    (filters, rois, labelIndex, galleryLabelIndex, assetIndex, changes, labels, selectedSet) =>
      RoiFilteringService.applyFilters({
        filters: filters.map((f) => ({ fieldId: f.field.id, operator: f.operator, value: f.value })),
        selectedCropIds: selectedSet,
        rois,
        changedRois: new Set(changes.keys()),
        labelIndex,
        galleryLabelIndex,
        labels,
        assetIndex,
      })
  );

  private readonly requiresChangedRoiHydration$ = this.roiStore.select(
    this.universalFilterStore.filters$,
    (filters) =>
      filters.some(
        (f) => f.value === 'true' && (f.field.id === 'changed' || f.field.id === 'unlabeled')
      )
  );

  private readonly missingChanges$ = this.roiStore.select(
    this.roiChangeStore.changedRois$,
    this.roiStore.rois$,
    (changes, rois) => ({
      changes,
      missing: Array.from(changes.keys()).filter((id) => !rois[id]),
    })
  );

  private readonly hydrateChangedRois = this.roiStore.effect((active$: Observable<boolean>) =>
    active$.pipe(
      distinctUntilChanged(),
      switchMap((active) =>
        active
          ? this.missingChanges$.pipe(
              first(({ missing }) => missing.length > 0),
              switchMap(({ changes }) => this.fetchAndUpsertChanges(changes))
            )
          : EMPTY
      )
    )
  );

  public readonly sectionCellCounts$ = this.roiStore.select(this.filteredLabelIndex$, (index) =>
    Array.from(index.values()).map((s) => s?.size ?? 0)
  );

  public readonly visibleSections$ = this.roiStore.select(this.filteredLabelIndex$, (index) =>
    Array.from(index.keys())
  );

  public readonly selectedLabelCounts$ = this.roiStore.select(
    this.universalFilterStore.filters$,
    this.itemSelectionStore.selectedItems$,
    this.filteredLabelIndex$,
    this.roiStore.rois$,
    (filters, selectedSet, labelIndex, rois) => this.getPanelSelectedCounts(filters, selectedSet, labelIndex, rois)
  );

  public readonly contextMenu$ = this.roiStore.select(
    this.itemSelectionStore.selectedItems$,
    this.roiStore.rois$,
    this.protocolStore.options$,
    this.protocolStore.categories$,
    (selectedSet, rois, labels, categories) => ({
      ...this.computeContextMenuLabels(selectedSet, rois),
      categories,
      labels,
    })
  );

  public readonly selectRoisByIndexPaths = this.roiStore.effect(
    (input$: Observable<{ indexPaths: GridIndexPath[]; cellsPerRow: number }>) =>
      input$.pipe(
        concatLatestFrom(() => [this.visibleSections$, this.filteredLabelIndex$]),
        tap(([{ indexPaths, cellsPerRow }, visibleSections, labelIndex]) => {
          const ids = this.resolveRoiIdsFromIndexPaths(indexPaths, cellsPerRow, visibleSections, labelIndex);
          this.itemSelectionStore.addSelectedItems(ids);
        })
      )
  );

  public readonly applyDragSelection = this.roiStore.effect(
    (input$: Observable<DragSelectionInput>) =>
      input$.pipe(
        concatLatestFrom(() => [this.visibleSections$, this.filteredLabelIndex$]),
        tap(([{ indexPaths, cellsPerRow, snapshot, modifiers }, visibleSections, labelIndex]) => {
          const liveIds = this.resolveRoiIdsFromIndexPaths(indexPaths, cellsPerRow, visibleSections, labelIndex);
          const next = this.deriveDragSelection(snapshot, liveIds, modifiers);
          this.itemSelectionStore.setSelectedItems(next);
        })
      )
  );

  private resolveRoiIdsFromIndexPaths(
    indexPaths: GridIndexPath[],
    cellsPerRow: number,
    visibleSections: string[],
    labelIndex: Map<string, Set<string>>
  ): string[] {
    const sectionArrays = new Map<string, string[]>();
    const ids: string[] = [];

    for (const indexPath of indexPaths) {
      const sectionId = visibleSections[indexPath.section];
      if (!sectionId) continue;

      let orderedIds = sectionArrays.get(sectionId);
      if (!orderedIds) {
        const crops = labelIndex.get(sectionId);
        if (!crops || crops.size === 0) continue;
        orderedIds = Array.from(crops);
        sectionArrays.set(sectionId, orderedIds);
      }

      const flatIndex = indexPath.row * cellsPerRow + indexPath.column;
      const id = orderedIds[flatIndex];
      if (id) ids.push(id);
    }

    return ids;
  }

  private deriveDragSelection(
    snapshot: ReadonlySet<string>,
    liveIds: string[],
    modifiers: DragModifiers
  ): Set<string> {
    if (modifiers.ctrlOrMeta) {
      const next = new Set(snapshot);
      for (const id of liveIds) (next.has(id) ? next.delete(id) : next.add(id));
      return next;
    }
    if (modifiers.shift) return new Set([...snapshot, ...liveIds]);
    return new Set(liveIds);
  }

  public setRoiBoxVisible(visible: boolean): void {
    this._roiBoxVisible$.next(visible);
  }

  public filterByLabel(labelName: string): void {
    const searchField = this.filterAutocompleteEffects.fieldMap.label;

    if (!searchField) return;

    const predicate = (f: Filter) => f.field.id !== 'label' && f.field.id !== 'unlabeled';
    const newTrailing = [{ field: searchField, operator: '=', value: labelName }];

    this.universalFilterStore.mergeFiltersPreserving({ predicate, newTrailing });
  }

  public filterByUnlabeled(): void {
    const searchField = this.filterAutocompleteEffects.fieldMap.unlabeled;
    if (!searchField) return;

    const predicate = (f: Filter) => f.field.id !== 'label' && f.field.id !== 'unlabeled';
    const newTrailing = [{ field: searchField, operator: '=', value: 'true' }];

    this.universalFilterStore.mergeFiltersPreserving({ predicate, newTrailing });
  }

  public filterByAsset(assetId: string): void {
    const searchField = this.filterAutocompleteEffects.fieldMap.asset;

    if (!searchField) return;

    const predicate = (f: Filter) => f.field.id !== 'asset';
    const newTrailing = [{ field: searchField, operator: '=', value: assetId }];

    this.universalFilterStore.mergeFiltersPreserving({ predicate, newTrailing });
  }

  public filterByChanged(): void {
    const changedField = this.filterAutocompleteEffects.fieldMap.changed;

    if (!changedField) return;

    const predicate = (f: Filter) => f.field.id !== 'changed';
    const newTrailing = [{ field: changedField, operator: '=', value: 'true' }];

    this.universalFilterStore.mergeFiltersPreserving({ predicate, newTrailing });
  }

  public filterBySelected(): void {
    const roiIdField = this.filterAutocompleteEffects.fieldMap.roiId;

    if (!roiIdField) return;

    const snapshot = this.itemSelectionStore.getSelectedItems();
    if (snapshot.size === 0) return;

    const predicate = (f: Filter) => f.field.id !== 'roiId';
    const newTrailing = [{ field: roiIdField, operator: 'in', value: JSON.stringify(Array.from(snapshot)) }];

    this.universalFilterStore.mergeFiltersPreserving({ predicate, newTrailing });
  }

  // #region GridViewDataSource

  public readonly numberOfSections = (): Observable<number> =>
    this.roiStore.select(this.sectionCellCounts$, (counts) => counts.length);

  public readonly numberOfCellsInSection = (section: number): Observable<number> =>
    this.roiStore.select(this.sectionCellCounts$, (counts) => counts[section] ?? 0);

  public readonly getCellForIndexPath = (
    indexPath: GridIndexPath,
    cellsPerRow: number,
    hoverLabel: string | undefined
  ): Observable<MosaicCellData> =>
    this.roiStore.select(
      this.visibleSections$,
      this.filteredLabelIndex$,
      this.roiStore.rois$,
      this.protocolStore.options$,
      this.itemSelectionStore.selectedItems$,
      this.roiChangeStore.changedRois$,
      this.assetStore.metadata$,
      this._roiBoxVisible$,
      (visibleSections, labelIndex, rois, labels, selectedSet, changes, assets, roiBoxVisible) => {
        const roi = this.getRoiForIndexPath(indexPath, cellsPerRow, visibleSections, labelIndex, rois);
        const label = labels[visibleSections[indexPath.section]];

        let isLabelHover = false;
        if (hoverLabel && roi) {
          const hoverLabelOption = labels[hoverLabel];
          const hoverRois = hoverLabelOption ? labelIndex.get(hoverLabelOption.id) : undefined;
          isLabelHover = !!hoverRois?.has(roi.id);
        }

        return {
          roiId: roi?.id ?? '',
          url: roi?.url ?? '',
          additionalLabels: roi?.labels?.size ?? 0,
          loading: !roi?.additionalLabelsIncluded,
          selected: selectedSet.has(roi?.id ?? ''),
          color: label?.color ?? 'black',
          displayPrimaryColor: isLabelHover && selectedSet.size > 1,
          changed: changes.has(roi?.id ?? ''),
          roiBox: roiBoxVisible ? this.getRoiBox(roi, assets) : undefined,
        };
      }
    );

  public readonly getRowNumberForIndexPath = (
    indexPath: IndexPath,
    cellsPerRow: number
  ): Observable<MosaicGalleryRowNumberData> =>
    this.roiStore.select(this.protocolStore.options$, this.visibleSections$, (labels, visibleSections) => ({
      rowNumber: indexPath.row * cellsPerRow + 1,
      color: labels?.[visibleSections[indexPath.section]]?.color ?? 'black',
    }));

  public readonly getHeaderForIndexPath = (index: number): Observable<MosaicGalleryHeaderData | undefined> =>
    this.roiStore.select(
      this.visibleSections$,
      this.protocolStore.options$,
      this.filteredLabelIndex$,
      this.roiStatsStore.labelCounts$,
      this.selectedLabelCounts$,
      this.itemSelectionStore.selectedItems$,
      (visibleSections, labels, labelIndex, labelCountIndex, selectedLabelCounts, selectedSet) => {
        const section = visibleSections[index];
        const label = section ? labels[section] : undefined;
        const loaded = labelIndex.get(section)?.size ?? 0;
        const isUnlabeled = section === 'unlabeled';

        switch (true) {
          case isUnlabeled:
            return this.getUnlabeledHeader(labelCountIndex, selectedLabelCounts, selectedSet);
          case !!label:
            return this.getLabelHeader(label, labelCountIndex, selectedLabelCounts, selectedSet);
          case !isUnlabeled && !label && loaded > 0:
            return this.getEmptyHeader(index, visibleSections, labelIndex, selectedLabelCounts, selectedSet);
          default:
            return undefined;
        }
      }
    );

  // #endregion

  // #region Private

  private wireHydrateChangedRois(): void {
    this.hydrateChangedRois(this.requiresChangedRoiHydration$);
  }

  private fetchAndUpsertChanges(changes: Map<string, RoiChange>): Observable<void> {
    const sample = this.pickSampleAndCreator(changes);
    if (!sample) return EMPTY;
    return from(this.listChangedRoisUseCase.execute(sample)).pipe(
      concatLatestFrom(() => [this.protocolStore.options$, this.roiStatsStore.ownSaveVersions$]),
      tap(([rois, options, ownSaves]) => {
        const filtered = this.filterInputs(rois, options);
        // Hydration upserts every changed roi, not only the missing ones, and its baseline is
        // the projection - so for a roi saved from here it can carry the pre-save labels.
        this.roiStore.upsertProjectedRois({ rois: new Map(filtered.map((r) => [r.id, r])), ownSaves });
      }),
      catchError((error) => {
        this.roiStore.setError(error.message);
        return EMPTY;
      }),
      map((): void => undefined)
    );
  }

  private filterInputs(rois: Roi[], options: LabelOptions): Roi[] {
    return rois.map((roi) => LabelingService.filterLabels(roi, options));
  }

  private pickSampleAndCreator(
    changes: Map<string, RoiChange>
  ): { sampleId: string; creatorId: string } | undefined {
    const firstChange = changes.values().next().value as RoiChange | undefined;
    return firstChange ? { sampleId: firstChange.sampleId, creatorId: firstChange.creatorId } : undefined;
  }

  private getRoiForIndexPath(
    indexPath: GridIndexPath,
    cellsPerRow: number,
    visibleSections: string[],
    labelIndex: Map<string, Set<string>>,
    rois: Dictionary<Roi>
  ): Roi | undefined {
    const sectionId = visibleSections[indexPath.section];
    const index = indexPath.row * cellsPerRow + indexPath.column;
    const crops = sectionId ? labelIndex.get(sectionId) ?? new Set<string>() : new Set<string>();
    const cropId = crops.size > 0 ? Array.from(crops)[index] : '';

    return rois[cropId];
  }

  /**
   * Where the ROI sits inside the crop the cell shows. The asset's pixel
   * dimensions decide it, and they only arrive once the asset's processing has
   * finished - until then the cell shows the crop with no box over it.
   */
  private getRoiBox(roi: Roi | undefined, assets: Map<string, IAssetItem>): RoiCropBox | undefined {
    if (!roi) return undefined;

    const data = assets.get(roi.assetId)?.data;

    return CropOverlayService.computeRoiBox(roi.coordinates, { width: data?.width, height: data?.height });
  }

  private getUnlabeledHeader(
    labelCountIndex: Map<string, number>,
    selectedLabelCounts: Map<string, number>,
    selectedSet: Set<string>
  ): MosaicGalleryHeaderData {
    return {
      title: 'label.unlabeled',
      total: labelCountIndex.get('unlabeled') ?? 0,
      selected: selectedLabelCounts.get('unlabeled') ?? 0,
      totalSelected: selectedSet.size,
    };
  }

  private getLabelHeader(
    label: Option,
    labelCountIndex: Map<string, number>,
    selectedLabelCounts: Map<string, number>,
    selectedSet: Set<string>
  ): MosaicGalleryHeaderData {
    return {
      title: label.name,
      total: labelCountIndex.get(label.id) ?? 0,
      selected: selectedLabelCounts.get(label.id) ?? 0,
      totalSelected: selectedSet.size,
    };
  }

  private getEmptyHeader(
    index: number,
    visibleSections: string[],
    labelIndex: Map<string, Set<string>>,
    selectedLabelCounts: Map<string, number>,
    selectedSet: Set<string>
  ): MosaicGalleryHeaderData {
    const section = visibleSections[index];

    return {
      title: '',
      total: labelIndex.get(section)?.size ?? 0,
      selected: selectedLabelCounts.get(section) ?? 0,
      totalSelected: selectedSet.size,
    };
  }

  private getPanelSelectedCounts(
    filters: Filter[],
    selectedCropIds: Set<string>,
    index: Map<string, Set<string>>,
    rois: Dictionary<Roi>
  ): Map<string, number> {
    return filters.length > 0
      ? this.getPanelSelectedCountsWithFilters(selectedCropIds, index.get(`panel`) ?? new Set())
      : this.getPanelSelectedLabelCounts(selectedCropIds, rois);
  }

  private getPanelSelectedLabelCounts(selectedCropIds: Set<string>, rois: Dictionary<Roi>): Map<string, number> {
    const selectedLabelCounts = new Map<string, number>();

    for (const roiId of selectedCropIds) {
      const roi = rois[roiId];

      if (!roi) continue;

      for (const label of roi.labels.keys()) {
        const currentCount = selectedLabelCounts.get(label) ?? 0;
        selectedLabelCounts.set(label, currentCount + 1);
      }

      if (roi.labels.size === 0) {
        const unlabeledCount = selectedLabelCounts.get(unlabeled) ?? 0;
        selectedLabelCounts.set(unlabeled, unlabeledCount + 1);
      }
    }

    return selectedLabelCounts;
  }

  private getPanelSelectedCountsWithFilters(selectedCropIds: Set<string>, index: Set<string>): Map<string, number> {
    const selectedLabelCounts = new Map<string, number>();

    let count = 0;

    for (const roiId of selectedCropIds) {
      if (!index.has(roiId)) continue;

      count++;
    }

    return selectedLabelCounts.set(`panel`, count);
  }

  private computeContextMenuLabels(
    selected: Set<string>,
    rois: Dictionary<Roi>
  ): { checkedOptions: string[]; doubleCheckedOptions: string[] } {
    const roiList = Array.from(selected)
      .map((id) => rois[id])
      .filter((roi): roi is Roi => roi !== undefined);

    if (roiList.length === 0) {
      return { checkedOptions: [], doubleCheckedOptions: [] };
    }

    const allLabelsSet = new Set<string>();
    for (const roi of roiList) {
      for (const labelId of roi.labels.keys()) {
        allLabelsSet.add(labelId);
      }
    }

    let commonLabels = new Set(roiList[0].labels.keys());
    for (let i = 1; i < roiList.length; i++) {
      const currentLabels = new Set(roiList[i].labels.keys());
      commonLabels = new Set([...commonLabels].filter((label) => currentLabels.has(label)));
    }

    return { checkedOptions: Array.from(allLabelsSet), doubleCheckedOptions: Array.from(commonLabels) };
  }
}
